import React from 'react';
import { ArrowRight, Star, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslations } from '../hooks/useTranslations';
export function Hero() {
  const {
    t,
    lang
  } = useTranslations();
  return <section id="about" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-white" data-id="element-251">
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#00E676 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-252" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-253">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-254">
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left" data-id="element-255">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 border border-green-100 text-[#00E676] text-sm font-medium mb-2" data-id="element-256">
              <span className="flex h-2 w-2 rounded-full bg-[#00E676] mr-2" data-id="element-257"></span>
              {t('hero.badge')}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-258">
              {lang === 'en' ? <>
                  {t('hero.title')}{' '}
                  <span className="text-[#00E676]" data-id="element-259">
                    {t('hero.titleHighlight')}
                  </span>{' '}
                  {t('hero.titleSuffix')}
                </> : <>
                  {t('hero.title')} {t('hero.titleSuffix')}
                </>}
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-260">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-261">
              <Button size="lg" className="group" onClick={() => document.getElementById('contact')?.scrollIntoView()} data-id="element-262">
                {t('nav.applyNow')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-263" />
              </Button>
              <div className="flex items-center text-sm font-medium text-gray-600" data-id="element-264">
                <div className="flex -space-x-2 mr-3" data-id="element-265">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-266">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-267" />
                    </div>)}
                </div>
                {t('hero.trustedBy')}
              </div>
            </div>

            <div className="pt-4 flex items-center justify-center lg:justify-start gap-6" data-id="element-268">
              <div className="flex items-center gap-2" data-id="element-269">
                <div className="flex text-[#00b67a]" data-id="element-270">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-current" data-id="element-271" />)}
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-272">
                  {t('hero.trustpilot')}
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Image Integrated with Background */}
          <div className="relative lg:h-[600px] flex items-center justify-center" data-id="element-273">
            <div className="relative w-full max-w-lg lg:max-w-none" data-id="element-274">
              {/* Main Image - PNG with transparent background integrated directly */}
              <div className="relative" data-id="element-275">
                <img src="https://cdn.magicpatterns.com/uploads/kGkyYG1wwa3dggLJRfv8Sm/vecteezy_business-woman-in-glasses-using-her-phone-on-transparent_47308872.png" alt="Professional Business Executive" className="w-full h-auto object-contain relative z-10" data-id="element-276" />

                {/* Floating Approval Badge 1 - Top Right (Balloon Style) */}
                <div className="absolute top-12 right-8 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 animate-bounce-slow hidden md:flex" data-id="element-277">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-278" />
                  <div data-id="element-279">
                    <p className="text-sm font-bold text-gray-900" data-id="element-280">$150,000</p>
                    <p className="text-xs text-gray-500" data-id="element-281">
                      {t('hero.approvalBadge1')}
                    </p>
                  </div>
                </div>

                {/* Floating Approval Badge 2 - Left Side (Balloon Style) */}
                <div className="absolute top-1/3 left-4 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 hidden md:flex" data-id="element-282">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-283" />
                  <div data-id="element-284">
                    <p className="text-sm font-bold text-gray-900" data-id="element-285">$200,000</p>
                    <p className="text-xs text-gray-500" data-id="element-286">
                      {t('hero.approvalBadge2')}
                    </p>
                  </div>
                </div>

                {/* Floating Approval Badge 3 - Bottom Right (Balloon Style) */}
                <div className="absolute bottom-20 right-12 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 hidden lg:flex" data-id="element-287">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-288" />
                  <div data-id="element-289">
                    <p className="text-sm font-bold text-gray-900" data-id="element-290">$75,000</p>
                    <p className="text-xs text-gray-500" data-id="element-291">
                      {t('hero.approvalBadge3')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative blob - subtle background enhancement */}
              <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-green-100 to-emerald-50 rounded-full blur-3xl opacity-40" data-id="element-292"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}